/* eslint-disable no-console */
import {Platform} from 'react-native';
import firebaseAnalytics from '@react-native-firebase/analytics';
import Tealium from 'tealium-react-native';
import {
  Collectors,
  ConsentPolicy,
  ConsentStatus,
  Dispatchers,
  TealiumEnvironment,
  TealiumEvent,
} from 'tealium-react-native/common';

import {APP_FIRST_LAUNCH} from '/enum/storage.enum';
import {BUTTON_TYPES, EVENT_NAMES} from '/enum/tealium.enum';
import {isProdEnvironment} from '/util/env.util';
import {getErrorTrackingUtilDefaultInstance} from '/util/error-tracking/error-tracking.util';

import {createStorage} from '../async-storage';

export const TEALIUM_CONFIG = {
  account: 'medifast',
  profile: 'optavia-connect-app',
  environment: isProdEnvironment()
    ? TealiumEnvironment.prod
    : TealiumEnvironment.dev,
  dispatchers: [
    Dispatchers.Collect,
    Dispatchers.TagManagement,
    Dispatchers.RemoteCommands,
  ],
  collectors: [
    Collectors.AppData,
    Collectors.DeviceData,
    Collectors.Lifecycle,
    Collectors.Connectivity,
  ],
  consentPolicy: ConsentPolicy.gdpr,
  visitorServiceEnabled: true,
};

export class TealiumConfig {
  isEnabled: any;

  isInitialized: any;

  private eventCache = new Map<string, number>();

  private readonly DEDUPE_TTL = 1000; // 1 second deduplication window

  constructor() {
    this.isInitialized = false;
    this.isEnabled = false;
  }

  // Initializes the tealium instance
  initialize() {
    if (Platform.OS === 'web') {
      const waitForUtag = setInterval(() => {
        if (window.utag) {
          clearInterval(waitForUtag);

          this.isInitialized = true;

          const originalTrack = Tealium.track;
          Tealium.track = (event: any) => {
            return originalTrack(event);
          };
        }
      }, 100);

      return;
    }

    Tealium.initialize(TEALIUM_CONFIG, success => {
      if (!success) {
        this.isInitialized = false;
        return;
      } else {
        this.isInitialized = true;

        /** added the below line because if we enable tracking through settings
        and launch the app,tealium event is not firing for the set consent **/

        if (this.isEnabled && this.isInitialized) {
          Tealium.setConsentStatus(ConsentStatus.consented);
        }
        const {getValue: getAppFirstLaunch} = createStorage({
          key: APP_FIRST_LAUNCH,
          defaultValue: null,
        });
        getAppFirstLaunch().then(isAppFirstLaunched => {
          if (!isProdEnvironment() && isAppFirstLaunched) {
            Tealium.setConsentStatus(ConsentStatus.consented);
            this.isEnabled = true;
          }
        });
      }

      // This sets up the piece that Tealium needs to correctly route the events through to Firebase
      Tealium.addRemoteCommand('firebase-analytics', payload => {
        const eventName = payload.firebase_event_name;
        const eventParameters = payload.firebase_event_params || {};

        if (eventName !== undefined) {
          // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
          firebaseAnalytics().logEvent(eventName, eventParameters);
        }
      });
    });
  }

  enable() {
    if (!this.isEnabled) {
      Tealium.setConsentStatus(ConsentStatus.consented);
    }
    this.isEnabled = true;
  }

  disable() {
    if (this.isEnabled) {
      Tealium.setConsentStatus(ConsentStatus.notConsented);
    }
    this.isEnabled = false;
  }

  /**
   * Generates a unique key for event deduplication
   */
  private generateEventKey(eventName: string, payload: any): string {
    // Create unique key from event name + critical payload fields
    const keyFields = [
      'screen_name',
      'user_id',
      'button_name',
      'current_screen_name',
      'user_type',
    ];
    const keyValues = keyFields
      .map(field => payload[field] || 'undefined')
      .join('|');
    return `${eventName}:${keyValues}`;
  }

  /**
   * Checks if event should be skipped due to deduplication
   */
  private shouldSkipDuplicate(eventName: string, payload: any): boolean {
    const key = this.generateEventKey(eventName, payload);
    const now = Date.now();
    const lastTrigger = this.eventCache.get(key);

    if (lastTrigger && now - lastTrigger < this.DEDUPE_TTL) {
      return true; // Skip duplicate
    }

    this.eventCache.set(key, now);
    return false;
  }

  /**
   * Safe event tracking with error handling and deduplication
   * Maintains backward compatibility by filtering null/undefined values like old implementation
   */
  safeTrack(eventName: string, payload: any): void {
    try {
      // Check for duplicates
      if (this.shouldSkipDuplicate(eventName, payload)) {
        return;
      }

      // Filter null/undefined values to maintain backward compatibility
      // This matches the old Object.fromEntries(filter(null)) behavior
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, value]) => value != null),
      );

      const event = new TealiumEvent(eventName, filteredPayload);
      Tealium.track(event);
    } catch (error) {
      // Log error but don't crash - maintain app stability
      getErrorTrackingUtilDefaultInstance().trackError(
        error,
        `Tealium tracking failed:, ${error}`,
      );
      // Optional: Could integrate with Splunk error logging here
    }
  }

  /**
   * Generates a Tealium event generator based on Event name passed in
   *
   * @param {object} params - Parameters
   * @param {string} params.eventName - One of EVENT_NAMES tealium.enum.js
   *
   * @returns {function} - Callback - A function to send the correct event type with correct params to tealium
   */
  generateTrackEvent({eventName}: any) {
    switch (eventName) {
      case EVENT_NAMES.navigationClick:
        return generateNavigationClick();
      case EVENT_NAMES.informationClick:
        return generateInformationClickEvent();
      case EVENT_NAMES.userClick:
        return generateUserClickEvent();
      case EVENT_NAMES.profileClick:
        return generateProfileClickEvent();
      case EVENT_NAMES.profileTabClick:
        return generateProfileTabClickEvent();
      case EVENT_NAMES.onPageLoad:
        return generateNavigationPageDetailsEvent();
      case EVENT_NAMES.exitLink:
        return generateExitLinkEvent();
      case EVENT_NAMES.onFilterPress:
        return generateFilterPressEvent();
      case EVENT_NAMES.onReportFilterPress:
        return generateReportFilterPressEvent();
      case EVENT_NAMES.sortEvent:
        return generateSortEvent();
      case EVENT_NAMES.checkboxClick:
        return generateCheckboxClickEvent();
      case EVENT_NAMES.notesEntry:
        return generateNotesEntryEvent();
      case EVENT_NAMES.contactClick:
        return generateContactClickEvent();
      case EVENT_NAMES.loginClick:
        return generateLoginClickedEvent();
      case EVENT_NAMES.loginError:
        return generateLoginErrorEvent();
      case EVENT_NAMES.forgotPasswordClick:
        return generateForgotPasswordClickedEvent();
      case EVENT_NAMES.forgetPasswordSubmit:
        return generateForgotPasswordSubmitClickedEvent();
      case EVENT_NAMES.profileUpdate:
        return generateProfileUpdateEvent();
      case EVENT_NAMES.checkIn:
        return generateCheckInClickEvent();
      case EVENT_NAMES.disposition:
        return generateDispositionEvent();
      case EVENT_NAMES.sharePlan:
        return generateMealPlanShare();
      case EVENT_NAMES.CTA_Click:
        return generateCTAClickEvent();
      case EVENT_NAMES.edge30Task:
        return generateEdge30Task();
      case EVENT_NAMES.edge30Entry:
        return generateEdge30Entry();
      case EVENT_NAMES.myTeamsFilters:
        return generateMyTeamApplyFiltersEvent();
      case EVENT_NAMES.searchTeamorg:
        return generateSearchTeamorg();
      default:
        return generateSendDefaultEvent({
          eventName,
        });
    }
  }
}

let TealiumUtilDefaultInstance: any = null;

export const getTealiumUtilDefaultInstance = () => {
  if (TealiumUtilDefaultInstance === null) {
    TealiumUtilDefaultInstance = new TealiumConfig();
  }

  return TealiumUtilDefaultInstance;
};
export function generateSearchTeamorg() {
  /**
   * Sends a userClick Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.screen_name
   * @param {string} params.search_term
   * @param {string} params.last_paid_rank
   * @param {string} params.coach_highest_rank
   * @param {string} params.coach_current_rank
   * @param {string} params.coach_id
   *
   *
   * @returns {null}
   */
  function sendSearchTeamorg({
    screenName,
    search_term,
    lastPaidRank,
    coachHighestRank,
    coachCurrentRank,
    coachID,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.searchTeamorg, {
      screen_name: screenName,
      search_term: search_term,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: coachHighestRank,
      coach_current_rank: coachCurrentRank,
      coach_id: coachID,
    });
    Tealium.track(event);
  }

  return sendSearchTeamorg;
}
/**
 * Generates a callback to send navigationEvent tealium tracking event
 *
 * @param {object} params - Parameters
 *
 * @returns {function} - Callback - A function to send the correct event type with correct params to tealium
 */
export function generateNavigationClick() {
  /**
   * Sends a navigationEvent Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.menuType - One of MENU_TYPES tealium.enum.js
   * @param {string} params.buttonName - title or name of button, ie the button text
   * @param {string} params.buttonType - One of BUTTON_TYPES tealium.enum.js
   * @param {string} params.screenName - Current React Navigation Route name
   * @param {string} params.last_paid_rank
   * @param {string} params.coach_highest_rank
   * @param {string} params.coach_current_rank
   * @param {string} params.coach_id
   * @param {string} params.user_type
   *
   *
   * @returns {null}
   */
  function sendNavigationEvent({
    menuType,
    buttonName,
    buttonType,
    screenName,
    lastPaidRank,
    coach_highest_rank,
    coach_current_rank,
    coach_id,
    userType,
    state,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.navigationClick, {
      user_type: userType,
      menu_type: menuType,
      button_name: buttonName,
      button_type: buttonType,
      screen_name: screenName,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: coach_highest_rank,
      coach_current_rank: coach_current_rank,
      coach_id: coach_id,
      state: state,
    });
    Tealium.track(event);
  }

  return sendNavigationEvent;
}

/**
 * Generates a callback to send informationClick tealium tracking event
 *
 * @param {object} params - Parameters
 *
 * @returns {function} - Callback - A function to send the correct event type with correct params to tealium
 */
export function generateInformationClickEvent() {
  /**
   * Sends an informationClick Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.buttonName - title or name of button, ie the button text
   * @param {string} params.screenName - Current React Navigation Route name
   * @param {string} params.last_paid_rank
   
   * @returns {null}
   */
  function sendInformationClickEvent({
    buttonName,
    screenName,
    lastPaidRank,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.informationClick, {
      button_type: BUTTON_TYPES.info,
      button_name: buttonName,
      screen_name: screenName,
      last_paid_rank: lastPaidRank,
    });
    Tealium.track(event);
  }

  return sendInformationClickEvent;
}

//share a plan event

export function generateCTAClickEvent() {
  /**
   * Sends a userClick Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.CTAtext
   * @param {string} params.CTAtype
   * @param {string} params.action
   * @param {string} params.targetLocation
   * @param {string} params.screenName
   * @param {string} params.last_paid_rank
   * @param {string} params.coach_highest_rank
   * @param {string} params.coach_current_rank
   * @param {string} params.coach_id
   *
   *
   * @returns {null}
   */
  function sendCTAClickEvent({
    screenName,
    CTAtext,
    CTAtype,
    action,
    targetLocation,
    lastPaidRank,
    coachHighestRank,
    coachCurrentRank,
    coachID,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.CTA_Click, {
      screen_name: screenName,
      cta_text: CTAtext,
      cta_type: CTAtype,
      action,
      target_location: targetLocation,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: coachHighestRank,
      coach_current_rank: coachCurrentRank,
      coach_id: coachID,
    });
    Tealium.track(event);
  }

  return sendCTAClickEvent;
}

//Edge30Task Event

export function generateEdge30Task() {
  /**
   * Sends a userClick Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.task
   * @param {string} params.action
   * @param {string} params.button_type
   * @param {string} params.task_number
   * @param {string} params.last_paid_rank
   * @param {string} params.coach_highest_rank
   * @param {string} params.coach_current_rank
   * @param {string} params.coach_id
   *
   *
   * @returns {null}
   */
  function sendEdge30Event({
    task,
    action,
    button_type,
    task_number,
    lastPaidRank,
    coachHighestRank,
    coachCurrentRank,
    coachID,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.edge30Task, {
      task: task,
      action: action,
      button_type: button_type,
      task_number: task_number,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: coachHighestRank,
      coach_current_rank: coachCurrentRank,
      coach_id: coachID,
    });
    Tealium.track(event);
  }

  return sendEdge30Event;
}

//Edge30Task Event Entery

export function generateEdge30Entry() {
  /**
   * Sends a userClick Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.screen_name
   * @param {string} params.entry_date
   * @param {string} params.todays_date
   * @param {string} params.action
   * @param {string} params.last_paid_rank
   * @param {string} params.coach_highest_rank
   * @param {string} params.coach_current_rank
   * @param {string} params.coach_id
   *
   *
   * @returns {null}
   */
  function sendEdge30EventEntery({
    screenName,
    entryDate,
    todaysDate,
    action,
    lastPaidRank,
    coachHighestRank,
    coachCurrentRank,
    coachID,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.edge30Entry, {
      screen_name: screenName,
      entry_date: entryDate,
      todays_date: todaysDate,
      action: action,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: coachHighestRank,
      coach_current_rank: coachCurrentRank,
      coach_id: coachID,
    });
    Tealium.track(event);
  }

  return sendEdge30EventEntery;
}
export function generateReportFilterPressEvent() {
  /**
   * Sends a filterEvent Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.filterType  - One of FILTER_TYPES tealium.enum.js
   * @param {string} params.currentScreenName 
   * @param {string} params.highestAchievedRank 
  * @param {string} params.reportName - Current report name
  * @param {string} params.last_paid_rank
  * * @param {string} params.coach_id
   
   * @returns {null}
   */
  function sendReportFilterEvent({
    filterType,
    reportName,
    currentScreenName,
    highestAchievedRank,
    lastPaidRank,
    coachId,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.onReportFilterPress, {
      filter_type: filterType,
      current_screen_name: currentScreenName,
      report_name: reportName,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: highestAchievedRank,
      coach_id: coachId,
    });

    Tealium.track(event);
  }

  return sendReportFilterEvent;
}

/**
 * Generates a callback to send userClick tealium tracking event
 *
 * @param {object} params - Parameters
 *
 * @returns {function} - Callback - A function to send the correct event type with correct params to tealium
 */
export function generateUserClickEvent() {
  /**
   * Sends a userClick Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.userType - One of USER_TYPES tealium.enum.js
   * @param {string} params.userId - coach or client id number
   * @param {string} params.userFullName - First and Last name of individual (ie John Smith)
   * @param {string} params.screenName - Current React Navigation Route name
   * @param {string} params.last_paid_rank
   * @param {string} params.coachHighestRank
   * @param {string} params.coachCurrentRank
   * @returns {null}
   */
  function sendUserClickEvent({
    userType,
    userId,
    userFullName,
    screenName,
    clientMilestone,
    currentScreenName,
    previousScreenName,
    lastPaidRank,
    coachHighestRank,
    coachCurrentRank,
  }: any) {
    const eventData: {[key: string]: any} = {
      user_type: userType,
      user_id: userId,
      user_full_name: userFullName,
      screen_name: screenName,
      client_milestone: clientMilestone,
      current_screen_name: currentScreenName,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: coachHighestRank,
      coach_current_rank: coachCurrentRank,
      previous_screen_name: previousScreenName,
    };
    // if (currentScreenName !== SCREEN_NAMES.coachTeamsReport) {
    //   eventData.previous_screen_name = previousScreenName;
    // }
    const event = new TealiumEvent(EVENT_NAMES.userClick, eventData);
    Tealium.track(event);
  }

  return sendUserClickEvent;
}
/**
 * Generates a callback to send profileClick tealium tracking event
 *
 * @param {object} params - Parameters
 *
 * @returns {function} - Callback - A function to send the correct event type with correct params to tealium
 */
export function generateProfileClickEvent() {
  /**
   * Sends a profileClick Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.userType - One of USER_TYPES tealium.enum.js
   * @param {string} params.userId - coach or client id number
   * @param {string} params.userFullName - First and Last name of individual (ie John Smith)
   * @param {string} params.last_paid_rank
   * @param {string} params.coachHighestRank
   * @param {string} params.coachCurrentRank
   * @param {string} params.screenName - Current React Navigation Route name
   * @param {string} params.target_location - Current location
   * @param {string} params.state - Current state
   * @param {string} params.coachId - Coach ID
   * @param {string} params.source - source if event triggered
   * @returns {null}
   */
  function sendProfileClickEvent({
    userType,
    userId,
    userFullName,
    screenName,
    lastPaidRank,
    coachHighestRank,
    coachCurrentRank,
    target_location,
    state,
    coachId,
    source,
  }: any) {
    const payload = {
      user_type: userType,
      user_id: userId,
      user_full_name: userFullName,
      screen_name: screenName,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: coachHighestRank,
      coach_current_rank: coachCurrentRank,
      target_location: target_location,
      state: state,
      coach_id: coachId,
      eventSource: source,
    };
    getTealiumUtilDefaultInstance().safeTrack(
      EVENT_NAMES.profileClick,
      payload,
    );
  }

  return sendProfileClickEvent;
}

export function generateDispositionEvent() {
  /**
   *
   * @param {object} params - Parameters
   * @param {string} params.userType
   * @param {string} params.userId
   * @param {string} params.newDisposition
   * @param {string} params.last_paid_rank
   * @param {string} params.coach_highest_rank
   * @param {string} params.coachId
   * @returns {null}
   */
  function sendDispositionClickEvent({
    userType,
    userId,
    newDisposition,
    lastPaidRank,
    coachHighestRank,
    coachId,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.disposition, {
      user_type: userType,
      user_id: userId,
      new_disposition: newDisposition,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: coachHighestRank,
      coach_id: coachId,
    });
    Tealium.track(event);
  }

  return sendDispositionClickEvent;
}

export function generateMealPlanShare() {
  /**
   *
   * @param {object} params - Parameters
   * @param {string} params.screen_name
   * @param {string} params.plan
   * @param {string} params.last_paid_rank
   * @param {string} params.coach_highest_rank
   * @param {string} params.coach_current_rank
   * @param {string} params.coach_id
   *
   *
   * @returns {null}
   */
  function sendshareMealPlanClickEvent({
    screenName,
    plan,
    lastPaidRank,
    coachHighestRank,
    coachCurrentRank,
    coachID,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.sharePlan, {
      screen_name: screenName,
      plan: plan,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: coachHighestRank,
      coach_current_rank: coachCurrentRank,
      coach_id: coachID,
    });
    Tealium.track(event);
  }

  return sendshareMealPlanClickEvent;
}
/**
 * Generates a callback to send profileTabClick tealium tracking event
 *
 * @param {object} params - Parameters
 *
 * @returns {function} - Callback - A function to send the correct event type with correct params to tealium
 */
export function generateProfileTabClickEvent() {
  /**
   * Sends a profileTabClick Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.userType - One of USER_TYPES tealium.enum.js
   * @param {string} params.userId - coach or client id number
   * @param {string} params.userFullName - First and Last name of individual (ie John Smith)
   * @param {string} params.tabName - title or name of tab, ie the button text
   * @param {string} params.last_paid_rank
   * @returns {null}
   */
  function sendProfileTabClickEvent({
    userType,
    userId,
    userFullName,
    tabName,
    lastPaidRank,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.profileTabClick, {
      user_type: userType,
      user_id: userId,
      user_full_name: userFullName,
      tab_name: tabName,
      last_paid_rank: lastPaidRank,
    });
    Tealium.track(event);
  }

  return sendProfileTabClickEvent;
}

/**
 * Generates a callback to send onPageLoad tealium tracking event
 *
 * @param {object} params - Parameters
 *
 * @returns {function} - Callback - A function to send the correct event type with correct params to tealium
 */
export function generateNavigationPageDetailsEvent() {
  /**
   * Sends a onPageLoad Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.currentScreenName - Current React Navigation Route name
   * @param {string} params.previousScreenName - Previous React Navigation Route name
   * @param {string} params.coach_highest_rank
   * @param {string} params.coach_current_rank
   * @param {string} params.coach_id
   * @param {string} params.last_paid_rank
   * @param {string} params.buttonType
   * @param {string} params.source source of the event triggered
   * @returns {null}
   */
  function sendOnPageLoadEvent({
    currentScreenName,
    previousScreenName,
    clientMilestone,
    coach_highest_rank,
    coach_current_rank,
    coach_id,
    lastPaidRank,
    buttonType,
    buttonName,
    screenName,
    source,
  }: any) {
    const eventData: {[key: string]: any} = {
      current_screen_name: currentScreenName,
      coach_highest_rank: coach_highest_rank,
      coach_current_rank: coach_current_rank,
      coach_id: coach_id,
      last_paid_rank: lastPaidRank,
      previous_screen_name: previousScreenName,
      screen_name: screenName,
      button_type: buttonType,
      ...(buttonName && {button_name: buttonName}),
      ...(clientMilestone && {client_milestone: clientMilestone}),
      eventSource: source,
      //...(buttonType && {button_type: buttonType}),
    };
    // Preserve all keys including null/undefined - don't filter them out
    getTealiumUtilDefaultInstance().safeTrack(
      EVENT_NAMES.onPageLoad,
      eventData,
    );
  }

  return sendOnPageLoadEvent;
}

/**
 * Generates a callback to send exitLink tealium tracking event
 *
 * @param {object} params - Parameters
 *
 * @returns {function} - Callback - A function to send the correct event type with correct params to tealium
 */
export function generateExitLinkEvent() {
  /**
   * Sends a exitLink Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.screenName - Current React Navigation Route name
   * @param {string} params.destinationLink - The link user is being prompted to visit
   * @param {boolean} params.userDecision - Whether the user chose to leave app by clicking the link
   * @param {string} params.last_paid_rank
   * @returns {null}
   */
  function sendExitLinkEvent({
    screenName,
    destinationLink,
    userDecision,
    lastPaidRank,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.exitLink, {
      screen_name: screenName,
      destination_link: destinationLink,
      user_decision: userDecision,
      last_paid_rank: lastPaidRank,
    });
    Tealium.track(event);
  }

  return sendExitLinkEvent;
}

/**
 * Generates a callback to send sortEvent tealium tracking event
 *
 * @param {object} params - Parameters
 *
 * @returns {function} - Callback - A function to send the correct event type with correct params to tealium
 */
export function generateSortEvent() {
  /**
   * Sends a sort Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.sortType - one of the analyticsTag value from sort.enum
   * @param {boolean} params.reportName - the report name
   * @param {string} params.filterType - the type of filter applied to the report
   * @param {string} params.last_paid_rank
   * @param {string} params.coach_highest_rank - the type of filter applied to the report
   * @param {string} params.coach_current_rank - the type of filter applied to the report
   * @param {string} params.coach_id
   * @returns {null}
   */
  function sendSortEvent({
    sortType,
    reportName,
    filterType,
    lastPaidRank,
    coach_highest_rank,
    coach_current_rank,
    coach_id,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.sortEvent, {
      sort_type: sortType,
      report_name: reportName,
      filter_type: filterType,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: coach_highest_rank,
      coach_current_rank: coach_current_rank,
      coach_id: coach_id,
    });
    Tealium.track(event);
  }

  return sendSortEvent;
}

/**
 * Generates a callback to send generic tealium tracking event
 *
 * @param {object} params - Parameters
 * @param {string} params.eventName - An event name to send to Tealium
 * @returns {function} - Callback - A function to send the correct event type with correct params to tealium
 */
export function generateSendDefaultEvent({eventName}: any) {
  /**
   * Sends a generic Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {object} params.eventParams - The params to send with the event
   *
   * @returns {null}
   */
  function sendDefaultEvent({eventParams}: any) {
    const event = new TealiumEvent(eventName, eventParams);
    Tealium.track(event);
  }

  return sendDefaultEvent;
}

/**
 * Generates a callback to send filterEvent tealium tracking event
 *
 * @param {object} params - Parameters
 *
 * @returns {function} - Callback - A function to send the correct event type with correct params to tealium
 */
export function generateFilterPressEvent() {
  /**
   * Sends a filterEvent Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.filterType  - One of FILTER_TYPES tealium.enum.js
   * @param {string} params.currentScreenName - Previous React Navigation Route name or parent screen name
   * @param {string} params.reportName - Current report name
  * @param {string} params.last_paid_rank
   
   * @returns {null}
   */
  function sendFilterEvent({
    filterType,
    filterSelected,
    filterType2,
    filterSelected2,
    currentScreenName,
    reportName,
    lastPaidRank,
    highestAchievedRank,
    coachCurrentRank,
    coachId,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.onFilterPress, {
      filter_type: filterType,
      current_screen_name: currentScreenName,
      report_name: reportName,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: highestAchievedRank,
      coach_current_rank: coachCurrentRank,
      filter_type2: filterType2,
      filter_selected: filterSelected,
      filter_selected2: filterSelected2,
      coach_id: coachId,
    });
    Tealium.track(event);
  }

  return sendFilterEvent;
}

export function generateCheckboxClickEvent() {
  /**
   * Sends a taskCheck Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.checkBoxName - One of client journey task
   * @param {string} params.checkedDate - current checked date
   * @param {string} params.currentScreenName - previous React Navigation Route name or parent screen name
   * @param {string} params.last_paid_rank
   
   * @returns {null}
   */
  function sendCheckboxClickEvent({
    checkboxName,
    checkedDate,
    currentScreenName,
    checkedStatus,
    lastPaidRank,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.checkboxClick, {
      checkbox_name: checkboxName,
      checked_date: checkedDate,
      current_screen_name: currentScreenName,
      last_paid_rank: lastPaidRank,
      ...(!checkedStatus && {unchecked: 1}),
    });
    Tealium.track(event);
  }

  return sendCheckboxClickEvent;
}
/**
 * Generates a callback to send notesEntry tealium tracking event
 *
 * @param {object} params - Parameters
 *
 * @returns {function} - Callback - A function to send the correct event type with correct params to tealium
 */
export function generateNotesEntryEvent() {
  /**
   * Sends a notesEntry Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.userType - One of USER_TYPES tealium.enum.js
   * @param {string} params.userId - candidate id number
   * @param {string} params.action - abandon, edit, new
   * @param {string} params.last_paid_rank
   
   * @returns {null}
   */
  function sendNotesEntryEvent({
    userType,
    userId,
    action,
    lastPaidRank,
    report_name,
    coach_highest_rank,
    coach_current_rank,
    coach_id,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.notesEntry, {
      user_type: userType,
      user_id: userId,
      action: action,
      last_paid_rank: lastPaidRank,
      report_name,
      coach_highest_rank,
      coach_current_rank,
      coach_id,
    });
    Tealium.track(event);
  }

  return sendNotesEntryEvent;
}
export function generateProfileUpdateEvent() {
  /**
   * Sends a notesEntry Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.updated_fields - One of updatedFields tealium.enum.js
   * @param {string} params.userId - candidate id number
   * @param {string} params.action - abandon, edit, new
   * @param {string} params.lastPaidRank
   * @param {string} params.coach_highest_rank
   * @param {string} params.coachCurrentRank
   * @param {string} params.coach_id
   * @param {string} params.report_name
   * @param {string} params.state
   * @param {string} params.screen_name
   * @param {string} params.user_full_name
   * @returns {null}
   */
  function sendProfileUpdateEvent({
    updated_fields,
    userId,
    action,
    lastPaidRank,
    coach_highest_rank,
    coachCurrentRank,
    coach_id,
    report_name,
    state,
    user_full_name,
    screen_name,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.profileUpdate, {
      updated_fields: updated_fields,
      user_id: userId,
      action: action,
      report_name: report_name,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: coach_highest_rank,
      coach_current_rank: coachCurrentRank,
      coach_id: coach_id,
      state,
      user_full_name,
      screen_name,
    });
    Tealium.track(event);
  }

  return sendProfileUpdateEvent;
}

export function generateLoginClickedEvent() {
  /**
   * Sends a loginClicked Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.eventName - An event name to send to Tealium
   * @param {string} params.success - Whether the login was successful
   * @param {string} params.error_state - Error state if login failed
   * @param {string} params.email
   * @param {string} params.login_enable
   * @param {string} params.coach_highest_rank
   * @param {string} params.coach_current_rank
   * @param {string} params.coach_id
   * @param {string} params.last_paid_rank
   
   * @returns {null}
   */
  function sendLoginClickedEvent({
    eventName,
    success,
    login_enable,
    error_state,
    email,
    coach_highest_rank,
    coach_current_rank,
    coach_id,
    last_paid_rank,
  }: {
    eventName: string;
    success: string;
    login_enable: string;
    error_state: string;
    email: string;
    coach_highest_rank: string;
    coach_current_rank: string;
    coach_id: string;
    last_paid_rank: string;
  }) {
    const event = new TealiumEvent(EVENT_NAMES.loginClick, {
      eventName: eventName,
      success: success,
      login_enable: login_enable,
      error_state: error_state,
      email: email,
      coach_highest_rank: coach_highest_rank,
      coach_current_rank: coach_current_rank,
      coach_id: coach_id,
      last_paid_rank: last_paid_rank,
    });
    Tealium.track(event);
  }
  return sendLoginClickedEvent;
}

export function generateLoginErrorEvent() {
  /**
   * Sends a loginError Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   
   * @returns {null}
   */
  function sendLoginErrorEvent({
    eventName,
    success,
    error_message,
  }: {
    eventName: string;
    success: string;
    error_message: string;
  }) {
    const event = new TealiumEvent(EVENT_NAMES.loginError, {
      eventName: eventName,
      success: success,
      error_message: error_message,
    });
    Tealium.track(event);
  }
  return sendLoginErrorEvent;
}

export function generateForgotPasswordClickedEvent() {
  /**
   * Sends a forgotPasswordClicked Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.eventName - An event name to send to Tealium
   * @param {string} params.error_state - Error state if the forgot password action fails
   
   * @returns {null}
   */
  function sendForgotPasswordClickedEvent({
    eventName,
    success,
    error_state,
    login_enable,
    email,
  }: {
    eventName: string;
    success: string;
    error_state: string;
    login_enable: string;
    email: string;
  }) {
    const event = new TealiumEvent(EVENT_NAMES.forgotPasswordClick, {
      eventName: eventName,
      success: success,
      error_state: error_state,
      login_enable: login_enable,
      email: email,
    });
    Tealium.track(event);
  }

  return sendForgotPasswordClickedEvent;
}
export function generateForgotPasswordSubmitClickedEvent() {
  /**
   * Sends a forgotPasswordClicked Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.eventName - An event name to send to Tealium
   * @param {string} params.error_state - Error state if the forgot password action fails
   
   * @returns {null}
   */
  function sendForgotPasswordSubmitClickedEvent({
    eventName,
    success,
    error_state,
    login_enable,
    email,
  }: {
    eventName: string;
    success: string;
    error_state: string;
    login_enable: string;
    email: string;
  }) {
    const event = new TealiumEvent(EVENT_NAMES.forgetPasswordSubmit, {
      eventName: eventName,
      success: success,
      error_state: error_state,
      login_enable: login_enable,
      email: email,
    });
    Tealium.track(event);
  }

  return sendForgotPasswordSubmitClickedEvent;
}
export function generateContactClickEvent() {
  /**
   * Sends a ContactClick Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.userType - One of USER_TYPES tealium.enum.js
   * @param {string} params.userId - candidate id number
   * @param {string} params.contact_method - call
   * @param {string} params.last_paid_rank
   
   * @returns {null}
   */
  function sendContactClickEvent({
    userType,
    userId,
    contact_method,
    lastPaidRank,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.contactClick, {
      user_type: userType,
      user_id: userId,
      contact_method: contact_method,
      last_paid_rank: lastPaidRank,
    });
    Tealium.track(event);
  }

  return sendContactClickEvent;
}

export function generateCheckInClickEvent() {
  /**
   * Sends a ContactClick Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.userType - One of USER_TYPES tealium.enum.js
   * @param {string} params.userId - candidate id number
   * @param {string} params.action - type of action
   * @param {string} params.last_paid_rank
   * @param {string} params.highest_achieved_rank
   * @param {string} params.coach_id
   * 
   
   * @returns {null}
   */
  function checkInClickEvent({
    userType,
    userId,
    action,
    lastPaidRank,
    highest_achieved_rank,
    coach_id,
  }: any) {
    const event = new TealiumEvent(EVENT_NAMES.checkIn, {
      user_type: userType,
      user_id: userId,
      action: action,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: highest_achieved_rank,
      coach_id: coach_id,
    });
    Tealium.track(event);
  }

  return checkInClickEvent;
}

export function generateMyTeamApplyFiltersEvent() {
  /**
   * Sends a filterEvent Tealium event with correct parameters
   *
   * @param {object} params - Parameters
   * @param {string} params.filterType  - One of FILTER_TYPES tealium.enum.js
   * @param {string} params.reportName - Current report name
   * @param {string} params.lastPaidRank
   * @param {string} params.coachHighestRank
   * @param {string} params.coach_id
   
   * @returns {null}
   */
  function sendMyTeamsFilterEvent({
    filter_type,
    filter_selected,
    filter_type2,
    filter_selected2,
    reportName,
    lastPaidRank,
    coachHighestRank,
    coachCurrentRank,
    coachId,
  }: any) {
    const rawEventData = {
      filter_type,
      report_name: reportName,
      last_paid_rank: lastPaidRank,
      coach_highest_rank: coachHighestRank,
      coach_current_rank: coachCurrentRank,
      filter_selected,
      coach_id: coachId,
      filter_type2,
      filter_selected2,
    };

    const cleanedEventData = Object.fromEntries(
      Object.entries(rawEventData).filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          value !== '' &&
          value !== 'undefined' &&
          value !== 'null',
      ),
    );
    const event = new TealiumEvent(
      EVENT_NAMES.myTeamsFilters,
      cleanedEventData,
    );
    Tealium.track(event);
  }

  return sendMyTeamsFilterEvent;
}
